import React from 'react'
import { WorkPeriodWithSummary, WorkPeriodWithSummaryComment } from '../../../api/work-periods'
import { Element, Icon } from 'react-bulma-components'
import ClockingRulesDescription from '../../clocking-rules/clocking-rules-description'
import { getEventsByType } from '../../../utils/work-periods'
import { formatTime } from '../../../utils/date'
import { UserCircleIcon } from '@heroicons/react/solid'
import Avatar from '../../avatar/avatar'

type CountedType = 'theoretical' | 'clocked' | 'manual'

const countedLabels: Record<CountedType, string> = {
  theoretical: 'Théorique',
  clocked: 'Pointage',
  manual: 'Corrigé',
}

interface WorkPeriodSummaryDateTooltipProps {
  workPeriod: WorkPeriodWithSummary
  type: 'start' | 'end'
}

/**
 * Composant pour afficher un commentaire de période de travail avec l'avatar de l'utilisateur
 */
const WorkPeriodComment: React.FC<{ comment: WorkPeriodWithSummaryComment }> = ({ comment }) => (
  <>
    {comment?.message} <br />
    {comment?.user && (
      <Element>
        <Icon size="small" style={{ verticalAlign: 'middle' }} mr={1}>
          <UserCircleIcon />
        </Icon>
        <Avatar user={comment.user} />
      </Element>
    )}
  </>
)

/**
 * Composant pour afficher une valeur formatée ou un placeholder
 */
const FormattedValue: React.FC<{
  value: string | number | Date | null | undefined
  formatter?: (value: string | number | Date) => string
}> = ({ value, formatter }) => {
  if (!value) return '/'
  return formatter ? formatter(value) : String(value)
}

export const WorkPeriodSummaryDateTooltipContent: React.FC<WorkPeriodSummaryDateTooltipProps> = ({
  workPeriod,
  type,
}) => {
  const benefitEvents = getEventsByType(workPeriod, 'benefit')
  const workPeriodHasBenefitEvent = Boolean(benefitEvents.length)
  const summary = workPeriod.summary[type]

  const items = [
    {
      label: 'Calculé',
      value: (
        <FormattedValue value={summary.counted} formatter={v => countedLabels[v as CountedType]} />
      ),
    },
    {
      label: 'Pointage',
      value: <FormattedValue value={summary.clocked} formatter={formatTime} />,
    },
    {
      label: 'Corrigé',
      value: <FormattedValue value={summary.manual} formatter={formatTime} />,
    },
    {
      label: 'Théorique',
      value: <FormattedValue value={summary.theoretical} formatter={formatTime} />,
    },
    {
      label: 'Commentaire',
      value: summary.lastComment ? (
        <WorkPeriodComment comment={summary.lastComment as WorkPeriodWithSummaryComment} />
      ) : (
        '/'
      ),
    },
  ]

  if (workPeriodHasBenefitEvent) {
    items.push({
      label: 'Evén. Prestation',
      value: <Element textWeight="bold">{benefitEvents.map(e => e.item.code).join(', ')}</Element>,
    })
  }

  return (
    <>
      {items.map(item => (
        <Element py={1} key={item.label} style={{ display: 'table-row' }}>
          <Element style={{ display: 'table-cell' }} px={1}>
            {item.label}
          </Element>
          <Element style={{ display: 'table-cell' }} px={1}>
            {item.value}
          </Element>
        </Element>
      ))}
      <Element mt={3} textWeight="bold">
        {workPeriod.clockingRules.name}
      </Element>
      <ClockingRulesDescription clockingRules={workPeriod.clockingRules} type={type} />
      <Element mt={3}>
        <Element renderAs="span" textWeight="bold">
          Affectation :
        </Element>{' '}
        {workPeriod.service?.name}
      </Element>
    </>
  )
}
